import React, { useState } from 'react';
import { Button } from '../Button';
import { ConfirmationModal } from '../ConfirmationModal';
import { ApiService, Referral } from '../../services/apiService';

const INDICI_ACK_ROLES = new Set(['admin', 'reception', 'receptionist', 'pa']);

interface SrIndiciCancelButtonProps {
  referral: Referral;
  userRole: string;
  onAcknowledged?: () => void;
}

export const SrIndiciCancelButton: React.FC<SrIndiciCancelButtonProps> = ({
  referral,
  userRole,
  onAcknowledged,
}) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = userRole?.toLowerCase() || '';
  const visible =
    INDICI_ACK_ROLES.has(role) &&
    referral.was_completed_at_cancel &&
    referral.sr_cancelled_at &&
    !referral.indici_cancel_acknowledged_at;

  if (!visible) return null;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const feed = await ApiService.getSrNotifications();
      const notification = feed.action_required.find(
        (item) => item.referral_id === referral.id
      );
      if (!notification) {
        setError('No action-required notification found for this referral.');
        return;
      }
      await ApiService.ackSrIndiciCancel(notification.id);
      window.dispatchEvent(new CustomEvent('sr-notifications-refresh'));
      setShowConfirm(false);
      onAcknowledged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button variant="danger" size="sm" onClick={() => setShowConfirm(true)} disabled={loading}>
        I have cancelled this referral in Indici
      </Button>
      {error && (
        <span style={{ color: 'var(--color-error, #c62828)', fontSize: '0.85rem', marginLeft: 8 }}>
          {error}
        </span>
      )}
      <ConfirmationModal
        isOpen={showConfirm}
        title="Confirm Indici cancellation"
        message="Confirm you have cancelled or updated this referral in Indici. This clears the action-required alert but keeps the cancellation banner for reference."
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        isLoading={loading}
      />
    </>
  );
};
